/**
 * Minimal toast system — a Solid signal of active toasts and a Portal
 * stack at the bottom-center of the viewport. Toasts auto-dismiss
 * after 4s; tap to dismiss early.
 *
 * Styles live in Toast.css.ts (vanilla-extract requires .css.ts files;
 * inline `style()` calls in .tsx blow up at runtime).
 */

import { createSignal, For, Show, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import * as css from './Toast.css';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

let nextId = 1;
const [toasts, setToasts] = createSignal<Toast[]>([]);

export function showToast(message: string, variant: ToastVariant = 'info', durationMs = 4000): void {
  const id = nextId++;
  setToasts((cur) => [...cur, { id, message, variant }]);
  if (durationMs > 0) {
    setTimeout(() => dismissToast(id), durationMs);
  }
}

export function dismissToast(id: number): void {
  setToasts((cur) => cur.filter((t) => t.id !== id));
}

export const ToastViewport: Component = () => {
  return (
    <Show when={toasts().length > 0}>
      <Portal>
        <div class={css.stack} role="status" aria-live="polite">
          <For each={toasts()}>
            {(t) => (
              <button
                type="button"
                class={`${css.toastBase} ${css.variants[t.variant]}`}
                onClick={() => dismissToast(t.id)}
              >
                {t.message}
              </button>
            )}
          </For>
        </div>
      </Portal>
    </Show>
  );
};
